import React, { FC } from 'react';
import { SvgIconProps } from '@mui/material';

import {
    Box, Stack, Typography, useDesktop, useMediaSwitch, useMobile, useTablet,
    FormPerson24, StackProps, Graph24, Wallet24, Bank24,
} from 'Modules/ui3';
import { fileUrl } from 'Modules/ui';

import Image1Desktop from './assets/Image1Desktop.png';
import Image1Tablet from './assets/Image1Tablet.png';
import Image1Mobile from './assets/Image1Mobile.png';
import Image2Tablet from './assets/Image2_Tablet.png';
import Image2Mobile from './assets/Image2_Mobile.png';
import Image2Desktop from './assets/Image2_Desktop.png';
import Image3Desktop from './assets/Image3_Desktop.png';
import Image3Tablet from './assets/Image3_Tablet.png';
import Image3Mobile from './assets/Image3_Mobile.png';
import Image4Desktop from './assets/Image4Desktop.png';
import Image4Tablet from './assets/Image4Tablet.png';
import Image4Mobile from './assets/Image4Mobile.png';


interface BadgeProps {
    badgeText: string;
    Icon: FC<SvgIconProps>;
    isMobile: boolean;
}

interface TextBlockProps extends BadgeProps, StackProps {
    title: string;
    description: string;
    isDesktop: boolean;
}
const Badge: FC<BadgeProps> = ({ Icon, badgeText, isMobile }) => (
    <Stack
        direction='row'
        bgcolor='grayscale.shade0'
        px={useMediaSwitch(6, 4, 3)}
        py={useMediaSwitch(3, 3, 2)}
        spacing={isMobile ? 2 : 3}
        borderRadius={5}
    >
        <Icon sx={{ fontSize: isMobile ? '20px' : '24px' }} />
        <Typography
            variant={isMobile ? 'mediumCaption1' : 'mediumBody2'}
            color='#242424'
            fontFamily='Hauora'
        >
            {badgeText}
        </Typography>
    </Stack>
);

const TextBlock: FC<TextBlockProps> = ({
    description, isMobile, isDesktop, title, badgeText, Icon, ...props
}) => (
    <Stack
        spacing={isMobile ? 5 : 8}
        alignItems='flex-start'
        {...props}
    >
        <Badge isMobile={isMobile} Icon={Icon} badgeText={badgeText} />
        <Stack spacing={isMobile ? 3 : 4}>
            <Typography
                variant={isMobile ? 'boldTitle5' : 'boldTitle4'}
                fontFamily='Hauora'
                fontWeight={700}
                lineHeight={isMobile ? 1.2 : 1.25}
                color='#242424'
            >
                {title}
            </Typography>
            <Typography
                fontFamily='Hauora'
                fontSize={isDesktop ? '18px' : '16px'}
                lineHeight='24px'
                fontWeight={500}
                color='#969592'
            >
                {description}
            </Typography>
        </Stack>
    </Stack>
);

const ItemWrapper: FC<StackProps> = props => (
    <Stack
        bgcolor='#F4F3F2'
        borderRadius={useMediaSwitch(12, 10, 8)}
        overflow='hidden'
        {...props}
    />
);

export const About:FC = () => {
    const isDesktop = useDesktop();
    const isTablet = useTablet();
    const isMobile = useMobile();
    return (
        <Stack
            spacing={useMediaSwitch(16, 12, 8)}
            alignItems='center'
            px={useMediaSwitch(12, 6, 2)}
            pt={useMediaSwitch(22, 16, 8)}
            pb={useMediaSwitch(22, 6, 2)}
            overflow='hidden'
            zIndex={2}
            bgcolor='#fff'
            {...!isDesktop && {
                sx: ({ spacing }) => ({
                    borderBottomLeftRadius: spacing(isTablet ? 10 : 8),
                    borderBottomRightRadius: spacing(isTablet ? 10 : 8),
                }),
            }}
        >
            <Box
                width='100%'
                maxWidth='1200px'
                {...isMobile && {
                    px: 3,
                }}
            >
                <Typography
                    fontFamily='Hauora'
                    fontSize={useMediaSwitch('72px', '56px', '32px')}
                    fontWeight={700}
                    lineHeight={isMobile ? '1.25' : '1.3'}
                    color='#242424'
                >
                    Почему ЦФА
                </Typography>
            </Box>
            <Stack spacing={isDesktop ? 2.5 : 2}>
                <Stack
                    width='100%'
                    maxWidth='1200px'
                    direction={isDesktop ? 'row' : 'column-reverse'}
                    spacing={2}
                >
                    <ItemWrapper
                        direction={!isTablet ? 'column' : 'row'}
                        {...isDesktop && { flexBasis: '420px' }}
                        alignItems={!isTablet ? 'center' : 'flex-start'}
                    >
                        <TextBlock
                            pl={isMobile ? 5 : 8}
                            pt={isMobile ? 5 : 8}
                            pr={useMediaSwitch(8, 0, 5)}
                            pb={useMediaSwitch(0, 8, 5)}
                            Icon={FormPerson24}
                            isMobile={isMobile}
                            isDesktop={isDesktop}
                            badgeText='Уверенность'
                            title='Проверка эмитентов'
                            description='Допуск к&nbsp;размещению только проверенных компаний'
                        />
                        <Box
                            height={useMediaSwitch('356px', '480px', '400px')}
                            width={useMediaSwitch('356px', '400px', '374px')}
                            flexShrink={0}
                            sx={{
                                background: `url(${fileUrl(useMediaSwitch(
                                    Image1Desktop,
                                    Image1Tablet,
                                    Image1Mobile,
                                ))}) center / cover no-repeat`,
                            }}
                        />
                    </ItemWrapper>
                    <ItemWrapper
                        direction={isMobile ? 'column' : 'row'}
                        position='relative'
                        {...isDesktop && {
                            flexBasis: '770px',
                        }}
                        {...isMobile && {
                            alignItems: 'center',
                        }}
                    >
                        <TextBlock
                            pl={isMobile ? 5 : 8}
                            pt={isMobile ? 5 : 8}
                            pr={useMediaSwitch(8, 0, 5)}
                            pb={isMobile ? 5 : 8}
                            Icon={Graph24}
                            isMobile={isMobile}
                            isDesktop={isDesktop}
                            {...isDesktop && {
                                maxWidth: '323px',
                            }}
                            badgeText='Доходность'
                            title='Комиссия 0%'
                            description='Премия к ключевой ставке и&nbsp;без&nbsp;комиссий посредникам'
                        />
                        <Box
                            {...isDesktop && {
                                position: 'absolute',
                                right: 0,
                                top: 0,
                            }}
                            height={useMediaSwitch('600px', '480px', '400px')}
                            width={useMediaSwitch('511px', '400px', '374px')}
                            flexShrink={0}
                            sx={{
                                background: `url(${fileUrl(useMediaSwitch(
                                    Image2Desktop, Image2Tablet, Image2Mobile,
                                ))}) center / cover no-repeat`,
                            }}
                        />
                    </ItemWrapper>
                </Stack>
                <Stack
                    width='100%'
                    maxWidth='1200px'
                    direction={isDesktop ? 'row' : 'column'}
                    spacing={2}
                >
                    <ItemWrapper
                        direction={isMobile ? 'column' : 'row'}
                        {...isDesktop && { flexBasis: '764px' }}
                        flexShrink={0}
                        alignItems={!isMobile ? 'flex-start' : 'center'}
                    >
                        <TextBlock
                            pl={isMobile ? 5 : 8}
                            pt={isMobile ? 5 : 8}
                            pr={useMediaSwitch(8, 7.5, 5)}
                            pb={isMobile ? 5 : 8}
                            Icon={Wallet24}
                            isMobile={isMobile}
                            isDesktop={isDesktop}
                            badgeText='Гибкость'
                            title='От 1 000 рублей'
                            description='ЦФА на&nbsp;любой срок от&nbsp;месяца до&nbsp;нескольких лет'
                        />
                        <Box
                            height={isMobile ? '400px' : '480px'}
                            width={useMediaSwitch('370px', '400px', '374px')}
                            flexShrink={0}
                            sx={{
                                background: `url(${fileUrl(useMediaSwitch(
                                    Image3Desktop, Image3Tablet, Image3Mobile,
                                ))}) center / cover no-repeat`,
                            }}
                        />
                    </ItemWrapper>
                    <ItemWrapper
                        direction={isTablet ? 'row' : 'column'}
                        {...!isMobile && {
                            justifyContent: 'space-between',
                        }}
                        {...isDesktop && {
                            flexBasis: '764px',
                        }}
                        alignItems='flex-start'
                    >
                        <TextBlock
                            pl={isMobile ? 5 : 8}
                            pt={isMobile ? 5 : 8}
                            pr={useMediaSwitch(8, 0, 5)}
                            pb={useMediaSwitch(0, 8, 5)}
                            Icon={Bank24}
                            isMobile={isMobile}
                            isDesktop={isDesktop}
                            badgeText='Удобство'
                            title='Свобода выбора'
                            description='Покупка ЦФА со&nbsp;счета любого банка'
                        />
                        <Box
                            height={useMediaSwitch('264px', '480px', '400px')}
                            width={useMediaSwitch('420px', '400px', '374px')}
                            flexShrink={0}
                            sx={{
                                background: `url(${fileUrl(useMediaSwitch(
                                    Image4Desktop,
                                    Image4Tablet,
                                    Image4Mobile,
                                ))}) center / cover no-repeat`,
                            }}
                        />
                    </ItemWrapper>
                </Stack>
            </Stack>
        </Stack>
    );
};
